import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Check, Copy, Download, GitCommitHorizontal, History, Package, RefreshCw, Search, Server } from 'lucide-react';
import { toast } from 'sonner';
import { getReleases } from '../../lib/api';
import { fetchHostedBuild, formatReleaseDate, isSameBuild, openedBuild, releaseMatches } from '../../lib/releases';

const groups = [
  ['added', 'Neu', 'bg-emerald-500'],
  ['improved', 'Verbessert', 'bg-sky-500'],
  ['fixed', 'Behoben', 'bg-amber-500'],
];

function Badge({ children, green = false }) {
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${green ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{children}</span>;
}

function Detail({ label, children }) {
  return <div className="min-w-0"><dt className="mb-1 text-xs text-slate-500">{label}</dt><dd className="break-words text-sm font-medium text-slate-800">{children || 'Nicht erfasst'}</dd></div>;
}

function SourceLink({ href, children }) {
  if (!href || !/^https:\/\/(github\.com|console\.cloud\.google\.com)\//.test(href)) return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 break-all text-emerald-700 underline-offset-4 hover:underline">{children}<ArrowUpRight className="h-3.5 w-3.5 shrink-0" /></a>;
}

function ReleaseCard({ release, current, latest }) {
  const used = isSameBuild(release, current);
  const hasNotes = Object.values(release.notes || {}).some(notes => notes.length);
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge><Package className="h-3.5 w-3.5" />v{release.version}</Badge>
          <Badge>{release.component === 'frontend' ? 'App' : 'Backend'}</Badge>
          {used && <Badge green><Check className="h-3 w-3" />In Verwendung</Badge>}
          {latest && <Badge green>Zuletzt veröffentlicht</Badge>}
        </div>
        <time dateTime={release.releasedAt} className="text-xs text-slate-500">Veröffentlicht · {formatReleaseDate(release.releasedAt)}</time>
      </div>
      <h3 className="text-lg font-semibold tracking-tight text-slate-900">{release.title}</h3>
      <div className="mt-5 space-y-5">
        {groups.map(([key, title, color]) => release.notes?.[key]?.length > 0 && (
          <section key={key}>
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold"><span className={`h-1.5 w-1.5 rounded-full ${color}`} />{title}</h4>
            <ul className="ml-3.5 space-y-1.5 text-sm leading-6 text-slate-600">{release.notes[key].map((note, index) => <li key={index}>{note}</li>)}</ul>
          </section>
        ))}
        {!hasNotes && <p className="text-sm text-slate-500">Weitere Änderungen sind über den zugehörigen Commit oder Versionsvergleich dokumentiert.</p>}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><GitCommitHorizontal className="h-4 w-4" /><SourceLink href={release.commitUrl}>{release.commit?.slice(0, 7) || 'Commit unbekannt'}</SourceLink></span>
        <span className="break-all">{release.branch || 'Branch nicht erfasst'}</span>
        {release.compareUrl && <SourceLink href={release.compareUrl}>Änderungen vergleichen</SourceLink>}
        <SourceLink href={release.buildUrl}>Build ansehen</SourceLink>
      </div>
      <details className="mt-4 text-xs text-slate-500">
        <summary className="cursor-pointer">Technische Details</summary>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          <Detail label="Build-ID">{release.buildId}</Detail>
          <Detail label="Build-Zeit">{formatReleaseDate(release.builtAt)}</Detail>
          <Detail label="Commit">{release.commit}</Detail>
          <Detail label="Quellstand">{release.sourceFingerprint}</Detail>
          <Detail label="Umgebung">{release.environment}</Detail>
          {release.revision && <Detail label="Cloud Run Revision">{release.revision}</Detail>}
        </dl>
      </details>
    </article>
  );
}

export default function AdminReleases() {
  const [data, setData] = useState(null);
  const [hosted, setHosted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [component, setComponent] = useState('all');
  const mounted = useRef(false);
  const busy = useRef(false);

  const refresh = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setError('');
    const [history, deployed] = await Promise.allSettled([getReleases(), fetchHostedBuild()]);
    if (mounted.current) {
      if (history.status === 'fulfilled') {
        if (history.value.historyStatus === 'unavailable') {
          setData(previous => previous ? { ...previous, current: history.value.current, historyStatus: 'unavailable' } : history.value);
          setError('Die Veröffentlichungshistorie ist derzeit nicht erreichbar. Bereits geladene Einträge bleiben sichtbar.');
        } else setData(history.value);
      } else setError('Versionen konnten nicht aktualisiert werden. Bitte erneut versuchen.');
      if (deployed.status === 'fulfilled') setHosted(deployed.value);
      else setHosted(null);
      setLoading(false);
    }
    busy.current = false;
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  const loadMore = async () => {
    if (busy.current || !data?.nextPageToken) return;
    busy.current = true;
    setLoadingMore(true);
    setError('');
    try {
      const next = await getReleases(data.nextPageToken);
      if (next.historyStatus !== 'available') throw new Error();
      if (mounted.current) setData(previous => ({ ...next, releases: [...previous.releases, ...next.releases.filter(item => !previous.releases.some(old => old.id === item.id))] }));
    } catch {
      if (mounted.current) setError('Weitere Veröffentlichungen konnten nicht geladen werden. Bitte erneut versuchen.');
    } finally {
      busy.current = false;
      if (mounted.current) setLoadingMore(false);
    }
  };

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ openedApp: openedBuild, hostedApp: hosted, backend: data?.current }, null, 2));
      toast.success('Versionsdetails kopiert');
    } catch { toast.error('Kopieren ist in diesem Browser nicht verfügbar. Die Details stehen auf dieser Seite.'); }
  };

  const exportHistory = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), complete: !data.nextPageToken, releases: data.releases }, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `fiscalpos-releases-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const records = data?.releases || [];
  const visible = records.filter(release => (component === 'all' || release.component === component) && releaseMatches(release, query));
  const newestApp = records.find(release => release.component === 'frontend');
  const newestBackend = records.find(release => release.component === 'backend');
  const updateAvailable = hosted && !isSameBuild(openedBuild, hosted);

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-4 pb-12 sm:p-8 lg:p-10" data-testid="releases-page">
      <header className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100/70 text-emerald-700"><History className="h-6 w-6" /></div>
        <div><p className="mb-1 text-xs font-semibold uppercase tracking-widest text-emerald-700">FiscalPOS · Fortschritt dokumentiert</p><h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Versionen & Updates</h1><p className="mt-2 text-sm leading-6 text-slate-500">Was veröffentlicht wurde, wann es live ging und welcher Push dahintersteht.</p></div>
      </header>

      {updateAvailable && <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"><span>Eine andere App-Version ist verfügbar: v{hosted.version}. Dein geöffnetes Fenster verwendet noch den unten gezeigten Build.</span><button onClick={() => window.location.reload()} className="shrink-0 font-semibold underline underline-offset-4">App neu laden</button></div>}

      <section aria-label="Aktuelle Version" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/50 p-5 sm:p-7">
          <div><p className="mb-2 text-sm text-slate-500">Deine geöffnete App-Version</p><div className="flex flex-wrap items-center gap-3"><h2 className="text-3xl font-semibold tracking-tight">v{openedBuild.version}</h2><Badge green={openedBuild.environment === 'production'}>{openedBuild.environment.toUpperCase()}</Badge>{openedBuild.dirty && <Badge>Lokale Änderungen</Badge>}</div></div>
          <button onClick={copyDetails} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium hover:bg-slate-50"><Copy className="h-4 w-4" />Details kopieren</button>
        </div>
        <dl className="grid gap-6 p-5 sm:grid-cols-2 sm:p-7 lg:grid-cols-3">
          <Detail label="Umgebung">{openedBuild.environment}</Detail><Detail label="Build-Zeit · Europe/Vienna">{formatReleaseDate(openedBuild.builtAt)}</Detail><Detail label="Commit"><SourceLink href={openedBuild.commitUrl}>{openedBuild.commit?.slice(0, 7) || 'Nicht erfasst'}</SourceLink></Detail>
          <Detail label="Build-ID">{openedBuild.buildId}</Detail><Detail label="Firebase Hosting">{openedBuild.hostingSite}</Detail><Detail label="Quellstand">{openedBuild.sourceFingerprint?.slice(0, 12)}</Detail>
        </dl>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 text-sm sm:px-7">
          <span className="flex items-center gap-2 text-slate-500"><Server className="h-4 w-4" />Laufendes Backend</span>
          <span className="flex flex-wrap items-center gap-2">{data?.current ? <><span>v{data.current.version}</span><span className="font-mono text-xs text-slate-500">{data.current.commit?.slice(0, 7)}</span><Badge green={isSameBuild(openedBuild, data.current)}>{isSameBuild(openedBuild, data.current) ? 'Gleicher Build' : 'Abweichender Build'}</Badge></> : loading ? 'Wird geprüft …' : 'Nicht erreichbar'}</span>
        </div>
      </section>

      <section aria-label="Veröffentlichungen" className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">Veröffentlichungen</h2><p className="mt-1 text-xs text-slate-500">Erfolgreiche Deployments · Zeitangaben in Europe/Vienna</p></div><div className="flex items-center gap-4">{records.length > 0 && <button onClick={exportHistory} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"><Download className="h-4 w-4" />{data.nextPageToken ? 'Geladene exportieren' : 'Exportieren'}</button>}<button disabled={loading || loadingMore} onClick={refresh} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Aktualisieren</button></div></div>
        {error && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div>}
        {data?.historyStatus === 'not_configured' && <div role="status" className="rounded-xl border border-slate-200 bg-white p-6 text-sm leading-6 text-slate-600">Die Veröffentlichungshistorie ist noch nicht eingerichtet. Build-Informationen sind bereits verfügbar. Veröffentlichungen erscheinen nach Einrichtung der automatischen Release-Pipeline.</div>}
        {records.length > 0 && <div className="flex flex-col gap-3 sm:flex-row"><label className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><input aria-label="Veröffentlichungen durchsuchen" value={query} onChange={event => setQuery(event.target.value)} placeholder="In geladenen Releases suchen: Version, Änderung oder Commit …" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm" /></label><select aria-label="Komponente" value={component} onChange={event => setComponent(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm"><option value="all">App & Backend</option><option value="frontend">App</option><option value="backend">Backend</option></select></div>}
        {loading && !data && <p role="status" className="py-10 text-center text-sm text-slate-500">Veröffentlichungen werden geladen …</p>}
        {!loading && data?.historyStatus === 'available' && records.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Noch keine erfolgreichen Veröffentlichungen erfasst.</p>}
        {records.length > 0 && visible.length === 0 && <p className="py-8 text-center text-sm text-slate-500">Keine passenden Veröffentlichungen in den geladenen Einträgen.</p>}
        {visible.map(release => <ReleaseCard key={release.id} release={release} current={release.component === 'frontend' ? openedBuild : data.current} latest={release.id === (release.component === 'frontend' ? newestApp : newestBackend)?.id} />)}
        {data?.nextPageToken && <button onClick={loadMore} disabled={loading || loadingMore} className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">{loadingMore ? 'Wird geladen …' : 'Ältere Veröffentlichungen laden'}</button>}
      </section>
    </div>
  );
}
